import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { push } from "connected-react-router";
import { ListContextProvider, useListContext } from "ra-core";
import { FC } from "react";
import { useDispatch } from "react-redux";
import { Content } from "~/components/content/Content";
import FunctionField from "~/components/field/FunctionField";
import TextField from "~/components/field/TextField";
import ClassificationLabel from "~/components/governance/labels/ClassificationLabel";
import { Datagrid } from "~/components/list/datagrid";
import { PaginationLimit } from "~/components/list/pagination";
import useLocalListController from "~/components/list/useLocalListController";
import RowExpandDataClassificationRuleEdit from "~/components/obfuscation/ClassificationProtection/RowExpandDataClassificationRuleEdit";
import Scrollbar from "~/components/Scrollbar";
import { ClassificationRuleAmbinetDataCotnextProvider, useClassificationRuleAmbinetDataCotnext } from "~/components/securefiles/ClassificationRuleAmbientDataProvider";
import { RecPatch } from "~/dataProvider/tools";
import { DataClassificationRuleDto } from "~/gen/api/encrypt";
import { OdResourcePageProps } from "~/types/resource";



const DataClassificationRules: FC<OdResourcePageProps> = (props) => {
  const listCtrls = useLocalListController({ resource: props.resource, basePath: '', perPage: 100000, page: 1 }, listOptions)
  
  return (
    <ClassificationRuleAmbinetDataCotnextProvider>
      <ListContextProvider value={listCtrls}>
        <DataClassificationRulesBody {...props} />
      </ListContextProvider>
    </ClassificationRuleAmbinetDataCotnextProvider>
  )
}

const DataClassificationRulesBody: FC<OdResourcePageProps> = ({
  resource, options,
}) => {
  const dispatch = useDispatch()
  const { classifications } = useClassificationRuleAmbinetDataCotnext()
  const listCtx = useListContext()

  return (
    <Content pageName="Data Classification Rules" resourceName="Data Classification Rules" breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create Rule
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2, }} >
        <Grid item xs={12}>
          <Card>
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  expand={<RowExpandDataClassificationRuleEdit />}
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& td.column-displayName span.MuiTypography-root' : { maxWidth:300, wordBreak:'break-word' },
                    '& td.column-comment span.MuiTypography-root' : { maxWidth:500, wordBreak:'break-word' },
                  }}
                  {...listCtx}
                >
                  <TextField label="Name" source="displayName" sortable={false} />
                  <FunctionField label="Description" source="comment" sortable={false} 
                    render={(d?:DataClassificationRuleDto&RecPatch) => (
                      <Typography variant="body2" component="pre" sx={{ lineBreak: 'anywhere', textWrap: 'auto' } as any}>{d?.comment}</Typography>
                    )}
                  />
                  <FunctionField source="targetClassification" label="Classification" sortable={false} 
                    render={(d?:DataClassificationRuleDto&RecPatch) => {
                      const clss = classifications.find(c => c.secondaryClassificationId === d?.targetClassification)
                      if (!clss) { return null }
                      return <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />;
                    }}
                  />
                  <FunctionField label="Priority" source="priority" sortable={false} 
                    render={(d?:DataClassificationRuleDto&RecPatch) => {
                      return (
                        <Typography variant="body2" component="span">
                          {d?.priority}
                          {!(d?.activeRule) &&
                            <Typography color="error" component="span"> / Inactive</Typography>}
                        </Typography>)
                    }}
                  />
                </Datagrid>
                {listCtx.loaded && listCtx.ids.length === 0 && <PaginationLimit />}
              </Box>
            </Scrollbar>
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default DataClassificationRules;

const defaultSorter = (a: DataClassificationRuleDto, b: DataClassificationRuleDto) => a.priority === b.priority ? (b.ruleId! - a.ruleId!) : (a.priority! - b.priority!)

const listOptions : {sorters: Record<string, (a:DataClassificationRuleDto & RecPatch, b:DataClassificationRuleDto & RecPatch) => number>} = {
  sorters: {
    id: defaultSorter,
  }
}
